import { useEffect, useRef, useState, useCallback } from 'react';
import type { SimulationSession } from '../../../features/sim-onboarding/types';

//#region AC_BLOCK use-sessions-sse -- Hook SSE para sessões de simulação multi-cidade em tempo real
// @ac-purpose Abre EventSource para /api/v1/simulation/sessions/stream e expõe sessões ativas
// @ac-related react-app/src/features/developer-cockpit/components/SessionCard.tsx#session-card

export interface UseSessionsSSEResult {
  sessions: SimulationSession[];
  connected: boolean;
  lastUpdate: Date | null;
  error: string | null;
  iniciar: (id: string) => Promise<void>;
  pausar: (id: string) => Promise<void>;
  retomar: (id: string) => Promise<void>;
  encerrar: (id: string) => Promise<void>;
  refresh: (mode?: 'active' | 'all' | 'STOPPED') => Promise<void>;
}

const API = (path: string) => `/api/v1${path}`;

function authHeaders(): HeadersInit {
  const token = localStorage.getItem('ac_token') || sessionStorage.getItem('ac_token');
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

async function sessionAction(method: string, path: string): Promise<void> {
  const r = await fetch(API(path), {
    method,
    credentials: 'include',
    headers: authHeaders(),
  });
  if (!r.ok) {
    const msg = await r.text().catch(() => r.statusText);
    throw new Error(msg);
  }
}

export function useSessionsSSE(enabled = true): UseSessionsSSEResult {
  const [sessions, setSessions] = useState<SimulationSession[]>([]);
  const [connected, setConnected] = useState(false);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  const esRef = useRef<EventSource | null>(null);
  const reconnectRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const backoffRef = useRef(2000);

  const connect = useCallback(() => {
    if (!enabled) return;
    if (esRef.current) {
      esRef.current.close();
      esRef.current = null;
    }

    // Cookie de sessão (withCredentials) — EventSource não aceita Authorization header
    const es = new EventSource(API('/simulation/sessions/stream'), { withCredentials: true });
    esRef.current = es;

    es.onopen = () => {
      setConnected(true);
      setError(null);
      backoffRef.current = 2000;
      if (reconnectRef.current) {
        clearTimeout(reconnectRef.current);
        reconnectRef.current = null;
      }
    };

    es.onmessage = (evt) => {
      try {
        const data = JSON.parse(evt.data) as { sessions: SimulationSession[]; ts: string };
        // SSE só traz ativas — mescla sem apagar STOPPED já carregados via REST
        setSessions((prev) => {
          const incoming = data.sessions ?? [];
          const stopped = prev.filter((s) => s.status === 'STOPPED');
          const byId = new Map<string, SimulationSession>();
          for (const s of stopped) byId.set(s.id, s);
          for (const s of incoming) byId.set(s.id, s);
          return Array.from(byId.values());
        });
        setLastUpdate(new Date(data.ts));
      } catch {
        // ignora mensagem malformada
      }
    };

    es.onerror = () => {
      setConnected(false);
      setError('SSE desconectado — reconectando…');
      es.close();
      esRef.current = null;
      const delay = Math.min(30000, backoffRef.current);
      backoffRef.current = Math.min(30000, backoffRef.current * 1.5);
      reconnectRef.current = setTimeout(connect, delay);
    };
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      esRef.current?.close();
      esRef.current = null;
      setConnected(false);
      return;
    }
    connect();
    return () => {
      esRef.current?.close();
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
    };
  }, [connect, enabled]);

  const refresh = useCallback(async (mode: 'active' | 'all' | 'STOPPED' = 'active') => {
    try {
      const path =
        mode === 'active'
          ? '/simulation/sessions/active'
          : mode === 'STOPPED'
            ? '/simulation/sessions?status=STOPPED'
            : '/simulation/sessions';
      const r = await fetch(API(path), {
        credentials: 'include',
        headers: authHeaders(),
      });
      if (!r.ok) {
        setError(`Falha ao listar sessões (${r.status})`);
        return;
      }
      const data = await r.json();
      setSessions(Array.isArray(data) ? data : []);
      setLastUpdate(new Date());
      setError(null);
    } catch (e: any) {
      setError(e?.message || 'Erro ao atualizar sessões');
    }
  }, []);

  useEffect(() => {
    refresh('active');
  }, [refresh]);

  const iniciar = async (id: string) => {
    await sessionAction('POST', `/simulation/sessions/${id}/start`);
    await refresh('active');
  };
  const pausar = async (id: string) => {
    await sessionAction('POST', `/simulation/sessions/${id}/pause`);
    await refresh('active');
  };
  const retomar = async (id: string) => {
    await sessionAction('POST', `/simulation/sessions/${id}/resume`);
    await refresh('active');
  };
  const encerrar = async (id: string) => {
    await sessionAction('POST', `/simulation/sessions/${id}/stop`);
    await refresh('all');
  };

  return { sessions, connected, lastUpdate, error, iniciar, pausar, retomar, encerrar, refresh };
}
//#endregion AC_BLOCK use-sessions-sse
